/**
 * 权限工具类
 * 提供权限相关的检查和处理方法
 */
import { useUserStore } from '@/store/modules/user'

/**
 * 检查用户是否拥有指定权限
 * @param {string} permission - 需要检查的权限标识
 * @returns {boolean} - 是否拥有权限
 */
export const hasPermission = (permission) => {
  try {
    const userStore = useUserStore()
    return userStore.hasPermission(permission)
  } catch (error) {
    console.error('检查权限出错:', error)
    
    // 当无法使用Store时，回退到使用localStorage检查
    try {
      // 检查是否为管理员
      const roles = JSON.parse(localStorage.getItem('roles') || '[]')
      const isAdmin = roles.includes('admin')
      
      // 管理员拥有所有权限
      if (isAdmin) {
        return true
      }
      
      // 检查具体权限
      const permissionsStr = localStorage.getItem('permissions')
      if (!permissionsStr) {
        return false
      }
      
      const permissions = JSON.parse(permissionsStr)
      return Array.isArray(permissions) && permissions.includes(permission)
    } catch (err) {
      console.error('回退方案检查权限出错:', err)
      return false
    }
  }
}

/**
 * 获取用户所有权限
 * @returns {string[]} - 权限列表
 */
export const getUserPermissions = () => {
  try {
    const userStore = useUserStore()
    return userStore.permissions
  } catch (error) {
    console.error('获取权限数据出错:', error)
    
    // 回退到localStorage
    try {
      const permissionsStr = localStorage.getItem('permissions')
      if (permissionsStr) {
        return JSON.parse(permissionsStr)
      }
    } catch (err) {
      console.error('从localStorage获取权限出错:', err)
    }
    return []
  }
}

/**
 * 检查用户是否可以访问指定页面
 * @param {string} pagePermission - 页面所需权限
 * @returns {boolean} - 是否可以访问
 */
export const canAccessPage = (pagePermission) => {
  return hasPermission(pagePermission)
}

/**
 * 根据权限过滤菜单项
 * @param {Array} menuItems - 菜单项列表
 * @returns {Array} - 过滤后的菜单
 */
export const filterMenuByPermission = (menuItems) => {
  if (!Array.isArray(menuItems)) {
    return []
  }
  
  return menuItems.filter(item => {
    // 没有权限要求的菜单项直接显示
    if (!item.permission) {
      return true
    }
    
    // 检查是否有权限
    return hasPermission(item.permission)
  })
}

/**
 * 获取用户可访问的第一个页面路径
 * @param {Array} pageList - 页面列表，格式为 [{path: '/path', permission: 'page:xxx'}]
 * @returns {string} - 可访问的页面路径，如果都不可访问则返回'/login'
 */
export const getFirstAccessiblePage = (pageList) => {
  if (!Array.isArray(pageList) || pageList.length === 0) {
    return '/login'
  }
  
  for (const page of pageList) {
    if (hasPermission(page.permission)) {
      return page.path
    }
  }
  
  return '/login'
}

export default {
  hasPermission,
  getUserPermissions,
  canAccessPage,
  filterMenuByPermission,
  getFirstAccessiblePage
} 